<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/composables';
import {
    BIcon, BListItemTitle, BListItem, BAutocomplete,
} from '@/design-system';
import type { FilterFieldMetadata } from '@/types/FilterMetadataTypes';

type MenuItem = {
    type: 'search' | 'field';
    text?: string;
    label: string;
    id: string;
} & Partial<FilterFieldMetadata>;

type PropsType = {
    fields: FilterFieldMetadata[];
    loading?: boolean;
    autofocus?: boolean;
    placeholder?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    loading: false,
    autofocus: false,
    placeholder: '',
});

const emit = defineEmits<{
    selectField: [fieldName: string];
    selectSearch: [searchText: string];
    'update:menu': [isOpen: boolean];
}>();

const { t } = useTranslation();

const inputValue = ref<string>('');
const menuOpen = ref(false);

const dynamicPlaceholder = computed(() => {
    return inputValue.value.trim() ? '' : props.placeholder;
});

const menuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    const query = inputValue.value.toLowerCase().trim();

    // Show matching fields
    const matchingFields = props.fields.filter(
        f => !query || f.label.toLowerCase().includes(query) || f.name.toLowerCase().includes(query),
    );

    for (const field of matchingFields) {
        items.push({
            type: 'field',
            label: field.label,
            name: field.name,
            operators: field.operators,
            id: `field-${field.name}`,
        });
    }

    // Show search option if we have input
    if (inputValue.value.trim()) {
        items.push({
            type: 'search',
            text: inputValue.value,
            label: t('filters.searchForText'),
            id: `search-${inputValue.value}`,
        });
    }

    return items;
});

const handleSelect = (value: MenuItem | MenuItem[] | null | undefined) => {
    if (!value || Array.isArray(value)) {
        return;
    }

    if (value.type === 'search' && value.text) {
        emit('selectSearch', inputValue.value);
        resetInput();
    } else if (value.type === 'field' && value.name) {
        emit('selectField', value.name);
        resetInput();
    }
};

const resetInput = () => {
    inputValue.value = '';
    menuOpen.value = false;
};

const handleEnter = () => {
    if (inputValue.value.trim()) {
        emit('selectSearch', inputValue.value);
        inputValue.value = '';
        menuOpen.value = false;
    }
};

const handleBackspace = (event: KeyboardEvent) => {
    if (inputValue.value && inputValue.value.length > 0) {
        event.stopPropagation();
    }
};

const handleMenuToggle = (value: boolean) => {
    if (!value) {
        // When menu is closed, reset everything
        resetInput();
    }
    menuOpen.value = value;
    emit('update:menu', value);
};
</script>

<template>
    <div class="intelligent-filter-input">
        <BAutocomplete
            :search="inputValue"
            :items="menuItems"
            item-title="label"
            item-value="id"
            return-object
            :autofocus="autofocus"
            :placeholder="dynamicPlaceholder"
            no-filter
            hide-details
            density="compact"
            variant="plain"
            :clearable="false"
            class="intelligent-autocomplete"
            :menu-props="{ width: 320, minWidth: 320 }"
            @update:model-value="handleSelect"
            @update:search="$event => (inputValue = $event)"
            @update:menu="handleMenuToggle"
            @keydown.enter="handleEnter"
            @keydown.delete="handleBackspace"
        >
            <template #item="{ props: itemProps, item }">
                <BListItem
                    v-bind="itemProps"
                    :title="undefined"
                    density="compact"
                >
                    <BListItemTitle>
                        <template v-if="item.raw.type === 'search'">
                            <BIcon
                                icon="mdi-magnify"
                                color="primary"
                                class="mr-2"
                            />
                        </template>
                        {{ item.raw.label }}
                    </BListItemTitle>
                </BListItem>
            </template>
        </BAutocomplete>
    </div>
</template>

<style lang="scss" scoped>
.intelligent-filter-input {
    flex: 1 1 auto;
    min-width: 220px;
    margin-right: 4px;
    position: relative;
}

:deep(.intelligent-autocomplete) {
    width: 100%;
}

:deep(.intelligent-autocomplete input::placeholder) {
    font-size: 0.9rem;
}

:deep(.intelligent-autocomplete .v-field__input) {
    padding: 0;
    min-height: 24px;
}

:deep(.intelligent-autocomplete .v-field) {
    background: transparent;
}

:deep(.intelligent-autocomplete .v-field__append-inner) {
    display: none;
}
</style>
